import React, { useState } from 'react';
import { auth } from '../firebase';
import { updateProfile, linkWithPopup, GoogleAuthProvider } from 'firebase/auth';
import Header from './Header';
import { useAuth } from '../contexts/AuthContext';
import { getRoleDisplayName } from '../utils/roleUtils';
import { apiFetch } from '../services/apiClient';
import { appConfig } from '../config';

const Profile = ({ onBack, onNavigateToHome, onNavigateToPrompts, onNavigateToWritersRoom, onLogOut, onNavigateToProfile, onModeSwitch, showAdminMode = false }) => {
  const [user] = useState(auth.currentUser);
  const { user: authUser, userRole, userOrganizations, getReportingData, refreshUserData } = useAuth();
  const [linkGoogleLoading, setLinkGoogleLoading] = useState(false);
  const [linkGoogleMessage, setLinkGoogleMessage] = useState(null);
  /* In-page message shown instead of browser alert() for the reset-password placeholder */
  const [resetPasswordMsg, setResetPasswordMsg] = useState(null);

  // Show "Link Google" if user has email/password but not Google (can then sign in with either)
  const hasPasswordProvider = authUser?.providerData?.some((p) => p.providerId === 'password');
  const hasGoogleProvider = authUser?.providerData?.some((p) => p.providerId === 'google.com');
  const canLinkGoogle = authUser && hasPasswordProvider && !hasGoogleProvider;
  const [dataActionMessage, setDataActionMessage] = useState(null);
  const [dataActionLoading, setDataActionLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const getOrganizationInfo = () => {
    if (userOrganizations && userOrganizations.length > 0) {
      const orgId = userOrganizations[0];
      const orgData = getReportingData(orgId);
      if (orgData && orgData.organizationInfo) {
        return {
          name: orgData.organizationInfo.name || 'Unknown Organization',
          state: orgData.organizationInfo.state || 'Unknown Location'
        };
      }
    }
    return { name: 'No Organization', state: 'Unknown Location' };
  };

  const organizationInfo = getOrganizationInfo();
  const [formData, setFormData] = useState({
    username: user?.displayName || '',
    email: user?.email || '',
    affiliation: organizationInfo.name
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    try {
      if (user) {
        await updateProfile(user, {
          displayName: formData.username
        });
      }
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
    }
  };

  const handleLogOutClick = () => {
    if (onLogOut) {
      onLogOut();
    }
  };

  const handleDownloadMyData = async () => {
    setDataActionLoading(true);
    setDataActionMessage(null);
    try {
      const data = await apiFetch('/api/admin/my-data', { authenticated: true });
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${appConfig.name.toLowerCase()}-my-data.json`;
      link.click();
      URL.revokeObjectURL(url);
      setDataActionMessage({ type: 'success', text: 'Your data has been downloaded.' });
    } catch (error) {
      setDataActionMessage({ type: 'error', text: error.message || 'Failed to export data.' });
    } finally {
      setDataActionLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm('This permanently deletes your account and progress. Continue?')) return;
    setDataActionLoading(true);
    try {
      await apiFetch('/api/admin/my-account', { method: 'DELETE', authenticated: true });
      handleLogOutClick();
    } catch (error) {
      setDataActionMessage({ type: 'error', text: error.message || 'Failed to delete account.' });
      setDataActionLoading(false);
    }
  };

  const handleNavigateToCourses = () => {
    onBack();
  };

  const handleResetPassword = () => {
    /* TODO: Implement password reset functionality */
    setResetPasswordMsg('Password reset functionality coming soon!');
  };

  /** Link Google account so user can sign in with either email/password or Google */
  const handleLinkGoogle = async () => {
    if (!authUser) return;
    setLinkGoogleLoading(true);
    setLinkGoogleMessage(null);
    try {
      const provider = new GoogleAuthProvider();
      await linkWithPopup(authUser, provider);
      setLinkGoogleMessage({ type: 'success', text: 'Google account linked! You can now sign in with either method.' });
      await refreshUserData?.();
    } catch (error) {
      if (error.code === 'auth/popup-closed-by-user') {
        setLinkGoogleMessage({ type: 'info', text: 'Linking cancelled.' });
      } else if (error.code === 'auth/credential-already-in-use') {
        setLinkGoogleMessage({
          type: 'error',
          text: 'That Google account is already linked to another Storykin account.',
        });
      } else {
        setLinkGoogleMessage({ type: 'error', text: error.message || 'Failed to link Google account.' });
      }
    } finally {
      setLinkGoogleLoading(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <Header 
        title="Profile" 
        onNavigateToHome={onNavigateToHome}
        onNavigateToCourses={handleNavigateToCourses}
        onNavigateToProfile={onNavigateToProfile}
        onNavigateToPrompts={onNavigateToPrompts}
        onNavigateToWritersRoom={onNavigateToWritersRoom}
        onLogOut={onLogOut}
        onModeSwitch={onModeSwitch}
        showAdminMode={showAdminMode}
      />

      {/* Main Content */}
      <div className="max-w-2xl mx-auto pt-4 md:pt-6 pb-12 px-4 sm:px-6 lg:px-8">
        {/* Profile Card */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          {/* Profile Fields */}
          <div className="space-y-6 mb-8">
            {/* Affiliation */}
            <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
              <div className="w-5 h-5 bg-gray-400 rounded flex items-center justify-center" aria-hidden="true">
                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
                </svg>
              </div>
              <span className="text-gray-700">{organizationInfo.name}</span>
            </div>

            {/* Location */}
            <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
              <div className="w-5 h-5 bg-gray-400 rounded flex items-center justify-center" aria-hidden="true">
                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
              </div>
              <span className="text-gray-700">{organizationInfo.state}</span>
            </div>

            {/* Role */}
            <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
              <div className="w-5 h-5 bg-blue-500 rounded flex items-center justify-center" aria-hidden="true">
                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" clipRule="evenodd" />
                  <path fillRule="evenodd" d="M4 5a2 2 0 012-2h8a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 3a1 1 0 000 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                </svg>
              </div>
              <span className="text-gray-700">
                {userRole ? getRoleDisplayName(userRole) : 'Loading...'}
              </span>
              {userRole && (
                <span className={`ml-2 px-2 py-1 rounded-full text-xs font-semibold ${
                  userRole === 'superadmin' ? 'bg-purple-100 text-purple-800' :
                  userRole === 'admin' ? 'bg-blue-100 text-blue-800' :
                  'bg-green-100 text-green-800'
                }`}>
                  {userRole.toUpperCase()}
                </span>
              )}
            </div>

            {/* Username */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-5 h-5 bg-gray-400 rounded-full flex items-center justify-center" aria-hidden="true">
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                  </svg>
                </div>
                {isEditing ? (
                  <>
                    <label htmlFor="profile-username" className="sr-only">Username</label>
                    <input
                      id="profile-username"
                      type="text"
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      className="bg-white border border-gray-300 rounded px-3 py-1 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </>
                ) : (
                  <span className="text-gray-700">{formData.username}</span>
                )}
              </div>
              <button
                onClick={() => setIsEditing(!isEditing)}
                aria-label={isEditing ? 'Cancel editing username' : 'Edit username'}
                className="min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-500 hover:text-gray-700"
              >
                <svg aria-hidden="true" className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
              </button>
            </div>

            {/* Email */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-5 h-5 bg-gray-400 rounded flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                </div>
                <span className="text-gray-700">{formData.email}</span>
              </div>
              {/* Email editing is not yet implemented; icon removed to avoid a non-functional interactive element (2.1.1 Keyboard) */}
            </div>
          </div>

          {/* Link Google Account - for email/password users who want to add SSO */}
          {canLinkGoogle && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-gray-700 mb-3">
                Link your Google account to sign in with either email/password or Google.
              </p>
              {/* role=status announces the link result to screen readers (WCAG 4.1.3) */}
              {linkGoogleMessage && (
                <p
                  role="status"
                  aria-live="polite"
                  className={`text-sm mb-3 ${
                    linkGoogleMessage.type === 'success'
                      ? 'text-green-700'
                      : linkGoogleMessage.type === 'error'
                      ? 'text-red-700'
                      : 'text-gray-600'
                  }`}
                >
                  {linkGoogleMessage.text}
                </p>
              )}
              <button
                onClick={handleLinkGoogle}
                disabled={linkGoogleLoading}
                className="flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {linkGoogleLoading ? 'Linking...' : 'Link Google Account'}
              </button>
            </div>
          )}

          {/* Reset Password Link */}
          <div className="mb-8">
            <button
              onClick={handleResetPassword}
              className="text-gray-700 underline hover:text-gray-900"
            >
              Reset Password
            </button>
            {/* Inline message replaces browser alert() — announced by screen readers via role=status */}
            {resetPasswordMsg && (
              <p
                role="status"
                aria-live="polite"
                className="mt-2 text-sm text-gray-600"
              >
                {resetPasswordMsg}
              </p>
            )}
          </div>

          {/* Data privacy */}
          <div className="mb-8 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-2">Your Data</h3>
            {dataActionMessage && (
              <p role="status" aria-live="polite" className={`text-sm mb-3 ${dataActionMessage.type === 'error' ? 'text-red-700' : 'text-green-700'}`}>
                {dataActionMessage.text}
              </p>
            )}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={handleDownloadMyData}
                disabled={dataActionLoading}
                className="min-h-[44px] px-4 py-2 border border-gray-300 rounded-lg hover:bg-white disabled:opacity-50"
              >
                Download My Data
              </button>
              <button
                type="button"
                onClick={handleDeleteAccount}
                disabled={dataActionLoading}
                className="min-h-[44px] px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 disabled:opacity-50"
              >
                Delete My Account
              </button>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-4">
            <button
              onClick={handleSave}
              className="w-full px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
            >
              Save
            </button>
            <button
              onClick={handleLogOutClick}
              className="w-full px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
            >
              Log Out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
